// File: frontend/src/pages/LMRosterManagementPage.tsx (Corrected)

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
    fetchLeagueDetails,
    fetchTeamsForRosterManagement,
    lmAddPlayerToTeamRoster,
    lmRemovePlayerFromTeamRoster,
    lmFinalizeRostersAndStartLeague
} from '@/api/leagues';
import { fetchTeamRoster } from '@/api/teams';
import { fetchAvailablePlayersForLeague, FetchAvailableLeaguePlayersParams } from '@/api/players';
import { LeagueRead, LMTeamRead, PlayerRead, RosterPlayerRead } from '@/api/schemas';
import toast from 'react-hot-toast';
import { useConfirmDialog } from '@/components/common/ConfirmDialog';
import Spinner from '@/components/common/Spinner';
import { mixpanelTrack } from '@/services/mixpanel';
import LeaguePlayerPool, { LeaguePositionFilter } from '@/components/league/LeaguePlayerPool';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { haveMatchingTeamIds, INCOMPLETE_TEAM_DATA_MESSAGE } from '@/utils/teamIntegrity';
import { UserActionableError } from '@/utils/appError';
import { userFacingError } from '@/utils/userFacingError';

const MAX_ROSTER_SIZE_LM = 15;
const logger = console;

const LMRosterManagementPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { token, user, isLoading: isAuthLoading, refreshUserContext } = useAuth();
  const showConfirm = useConfirmDialog();
  const navigate = useNavigate();

  const [leagueDetails, setLeagueDetails] = useState<LeagueRead | null>(null);
  const [teams, setTeams] = useState<LMTeamRead[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<LMTeamRead | null>(null);
  const [fetchedTeamRoster, setFetchedTeamRoster] = useState<RosterPlayerRead[]>([]);
  
  const [availablePlayersPool, setAvailablePlayersPool] = useState<PlayerRead[]>([]);
  const [isLoadingPage, setIsLoadingPage] = useState<boolean>(true);
  const [isLoadingSelectedTeamRoster, setIsLoadingSelectedTeamRoster] = useState<boolean>(false);
  const [pageError, setPageError] = useState<string | null>(null);
  
  const [currentPlayerPoolSkip, setCurrentPlayerPoolSkip] = useState<number>(0);
  const [totalAvailablePlayersInPool, setTotalAvailablePlayersInPool] = useState<number>(0);
  const [hasMorePlayersInPool, setHasMorePlayersInPool] = useState<boolean>(false);
  const [isLoadingMorePlayers, setIsLoadingMorePlayers] = useState<boolean>(false);
  const PLAYER_POOL_PAGE_LIMIT = 50;
  const [completePlayerCache, setCompletePlayerCache] = useState<Map<number, PlayerRead>>(new Map());
  const [isUpdatingRoster, setIsUpdatingRoster] = useState<boolean>(false);
  const [isFinalizing, setIsFinalizing] = useState<boolean>(false);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  const [finalizeSuccessMessage, setFinalizeSuccessMessage] = useState<string | null>(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [positionFilter, setPositionFilter] = useState<LeaguePositionFilter>('ALL');
  const [sortConfig, setSortConfig] = useState<{ sortBy: string; sortOrder: 'asc' | 'desc' }>({ sortBy: 'fppg', sortOrder: 'desc' });
  
  const numericLeagueId = leagueId ? parseInt(leagueId, 10) : null;

  const currentTeamRosterPlayerIds = useMemo(() => {
    return new Set(fetchedTeamRoster.map(rp => rp.player_id));
  }, [fetchedTeamRoster]);

  useEffect(() => {
    if (isAuthLoading || !numericLeagueId || !token || !user) return;

    const loadInitialData = async () => {
        setIsLoadingPage(true);
        setPageError(null);
        setTeams([]);
        setSelectedTeam(null);
        try {
            const leagueData = await fetchLeagueDetails(numericLeagueId, token);
            setLeagueDetails(leagueData);

            if (user.id !== leagueData.owner_id) {
                setPageError("Access Denied: You are not the manager of this league.");
                navigate(`/leagues/${numericLeagueId}/my-team`, { replace: true });
                return;
            }

            const teamsDataFromApi = await fetchTeamsForRosterManagement(numericLeagueId, token);
            if (!haveMatchingTeamIds(leagueData.teams, teamsDataFromApi)) {
                throw new UserActionableError(INCOMPLETE_TEAM_DATA_MESSAGE);
            }
            setTeams(teamsDataFromApi);

            const initialPoolParams: FetchAvailableLeaguePlayersParams = {
              leagueId: numericLeagueId, token: token, skip: 0, limit: PLAYER_POOL_PAGE_LIMIT,
              sort_by: 'fppg', sort_order: 'desc', availability: 'available'
            };
            const initialPlayersResponse = await fetchAvailablePlayersForLeague(initialPoolParams);
            setAvailablePlayersPool(initialPlayersResponse.players);
            setTotalAvailablePlayersInPool(initialPlayersResponse.total_available);
            setHasMorePlayersInPool(initialPlayersResponse.has_more);
            setCurrentPlayerPoolSkip(initialPlayersResponse.players.length);

        } catch (err: unknown) {
            setPageError(userFacingError(err, "Failed to load page data."));
        } finally {
            setIsLoadingPage(false);
        }
    };

    loadInitialData();
  }, [numericLeagueId, token, user, isAuthLoading, navigate]);

  useEffect(() => {
    const fetchRosterForSelectedTeam = async () => {
      if (selectedTeam && selectedTeam.id && token) {
        setIsLoadingSelectedTeamRoster(true);
        setFetchedTeamRoster([]);
        try {
          const rosterDataFromApi = await fetchTeamRoster(selectedTeam.id, token);
          const correctedRoster = rosterDataFromApi.roster.map(apiRosterEntry => {
            const playerId = apiRosterEntry.player?.id;
            if (!playerId) return apiRosterEntry;
            const cachedCompletePlayer = completePlayerCache.get(playerId);
            if (cachedCompletePlayer) return { ...apiRosterEntry, player: cachedCompletePlayer };
            const playerFromPool = availablePlayersPool.find(p => p.id === playerId);
            if (playerFromPool) return { ...apiRosterEntry, player: playerFromPool };
            return apiRosterEntry;
          });
          setFetchedTeamRoster(correctedRoster || []);
        } catch (err: unknown) {
          setPageError(userFacingError(err, `Failed to load roster for ${selectedTeam.name}`));
          setFetchedTeamRoster([]);
        } finally {
          setIsLoadingSelectedTeamRoster(false);
        }
      } else {
        setFetchedTeamRoster([]);
      }
    };
    fetchRosterForSelectedTeam();
  }, [selectedTeam, token, completePlayerCache, availablePlayersPool]);

  const handleSelectTeamChange = (teamIdStr: string) => {
    const teamIdNum = parseInt(teamIdStr, 10);
    const teamToSelect = teams.find(t => t.id === teamIdNum) || null;
    setSelectedTeam(teamToSelect);
    if (!teamToSelect) setFetchedTeamRoster([]);
    setFinalizeError(null); setFinalizeSuccessMessage(null);
  };

  const handleLoadMorePlayers = async () => {
    if (!numericLeagueId || !token || !hasMorePlayersInPool || isLoadingMorePlayers) return;
    setIsLoadingMorePlayers(true);
    setPageError(null);
    try {
      const params: FetchAvailableLeaguePlayersParams = {
        leagueId: numericLeagueId, token: token, skip: currentPlayerPoolSkip, limit: PLAYER_POOL_PAGE_LIMIT,
        sort_by: 'fppg', sort_order: 'desc', availability: 'available'
      };
      const paginatedResponse = await fetchAvailablePlayersForLeague(params);
      setAvailablePlayersPool(prevPlayers => [...prevPlayers, ...paginatedResponse.players]);
      setTotalAvailablePlayersInPool(paginatedResponse.total_available);
      setHasMorePlayersInPool(paginatedResponse.has_more);
      setCurrentPlayerPoolSkip(prevSkip => prevSkip + paginatedResponse.players.length);
      mixpanelTrack('LM Player Pool Load More', { 'User ID': user?.id, 'League ID': numericLeagueId });
    } catch (err: unknown) {
      setPageError(userFacingError(err, "Failed to load more players."));
    } finally {
      setIsLoadingMorePlayers(false);
    }
  };
  
  // --- THIS IS THE CORRECTED FUNCTION SIGNATURE ---
  const handleAddPlayerToSelectedTeam = useCallback(async (playerToAdd: PlayerRead) => {
      if (!selectedTeam || !selectedTeam.id || !token || !numericLeagueId) {
          logger.error("Add player pre-conditions not met.");
          return;
      }
      if (fetchedTeamRoster.length >= MAX_ROSTER_SIZE_LM) {
          toast.error(`Roster for ${selectedTeam.name} is full (${MAX_ROSTER_SIZE_LM} players).`);
          return;
      }
      setIsUpdatingRoster(true);
      setPageError(null);
      try {
          const incompleteRosterEntryFromApi = await lmAddPlayerToTeamRoster(numericLeagueId, selectedTeam.id, playerToAdd.id, token);
          mixpanelTrack('LM Added Player to Roster', { 'User ID': user?.id, 'League ID': numericLeagueId, 'Team ID': selectedTeam.id, 'Player ID': playerToAdd.id });
          const completeRosterEntryForState: RosterPlayerRead = { ...incompleteRosterEntryFromApi, player: playerToAdd };
          setCompletePlayerCache(prev => new Map(prev).set(playerToAdd.id, playerToAdd));
          setFetchedTeamRoster(prevRoster => [...prevRoster, completeRosterEntryForState]);
          setAvailablePlayersPool(prevPool => prevPool.filter(p => p.id !== playerToAdd.id));
          setTotalAvailablePlayersInPool(prevTotal => Math.max(0, prevTotal - 1));
      } catch (err: unknown) {
          setPageError(userFacingError(err, `Failed to add player.`));
      } finally {
          setIsUpdatingRoster(false);
      }
  }, [selectedTeam, token, numericLeagueId, user?.id, fetchedTeamRoster]);
  // --- END CORRECTION ---

  const handleRemovePlayerFromSelectedTeam = useCallback(async (playerIdToRemove: number) => {
    if (!selectedTeam || !selectedTeam.id || !token || !numericLeagueId) return;
    const playerObjectInRoster = fetchedTeamRoster.find(rp => rp.player?.id === playerIdToRemove)?.player;
    if (!playerObjectInRoster) {
        setPageError(`Player ${playerIdToRemove} not found on team ${selectedTeam.name}'s roster.`);
        return;
    }
    setIsUpdatingRoster(true);
    setPageError(null);
    try {
        await lmRemovePlayerFromTeamRoster(numericLeagueId, selectedTeam.id, playerIdToRemove, token);
        mixpanelTrack('LM Removed Player from Roster', { 'User ID': user?.id, 'League ID': numericLeagueId, 'Team ID': selectedTeam.id, 'Player ID': playerIdToRemove });
        setFetchedTeamRoster(prevRoster => prevRoster.filter(rp => rp.player?.id !== playerIdToRemove));
        setAvailablePlayersPool(prevPool => {
            const newPool = [...prevPool, playerObjectInRoster];
            newPool.sort((a, b) => (b.fppg ?? -Infinity) - (a.fppg ?? -Infinity));
            return newPool;
        });
        setTotalAvailablePlayersInPool(prevTotal => prevTotal + 1);
    } catch (err: unknown) {
        setPageError(userFacingError(err, `Failed to remove player.`));
    } finally {
        setIsUpdatingRoster(false);
    }
  }, [selectedTeam, token, numericLeagueId, user?.id, fetchedTeamRoster]);

  const teamDataIsComplete = useMemo(() => {
    if (!leagueDetails) return false;
    return haveMatchingTeamIds(leagueDetails.teams, teams);
  }, [teams, leagueDetails]);

  const leagueIsFull = !!leagueDetails && leagueDetails.teams.length === leagueDetails.max_teams;
  const canAttemptFinalize = teamDataIsComplete && leagueIsFull;

  const handleFinalizeAndStartLeague = async () => {
    if (!numericLeagueId || !token || !user || !leagueDetails) return;
    if (!teamDataIsComplete) { setFinalizeError(INCOMPLETE_TEAM_DATA_MESSAGE); return; }
    if (!leagueIsFull) { setFinalizeError("Cannot start league: Ensure all team slots are filled."); return; }
    if (leagueDetails.draft_status === 'REGULAR_SEASON' || leagueDetails.draft_status === 'PLAYOFFS' || leagueDetails.draft_status === 'COMPLETED') { setFinalizeError(`League is already in '${leagueDetails.draft_status}' status.`); return; }
    const confirmation = await showConfirm({ message: "Are you sure you want to finalize rosters and start the league season? This cannot be undone.", confirmLabel: 'Finalize', destructive: true });
    if (!confirmation) return;
    setIsFinalizing(true); setFinalizeError(null); setFinalizeSuccessMessage(null);
    mixpanelTrack('LM Finalize & Start League Attempt', { 'User ID': user.id, 'League ID': numericLeagueId });
    try {
      const updatedLeague = await lmFinalizeRostersAndStartLeague(numericLeagueId, token);
      setLeagueDetails(updatedLeague);
      setFinalizeSuccessMessage(`League "${updatedLeague.name}" started successfully! Redirecting...`);
      mixpanelTrack('LM Finalize & Start League Success', { 'User ID': user.id, 'League ID': numericLeagueId });
      await refreshUserContext();
      setTimeout(() => { navigate(`/leagues/${numericLeagueId}/my-team`, { replace: true }); }, 2500);
    } catch (err: unknown) {
      setFinalizeError(userFacingError(err, "Failed to start league season."));
    } finally {
      setIsFinalizing(false);
    }
  };

  const isLmPreDraftContext = useMemo(() => {
    if (leagueDetails && user && leagueDetails.owner_id === user.id) {
      return leagueDetails.draft_status === 'PRE_DRAFT' || leagueDetails.draft_status === 'ROSTER_INPUT_ACTIVE';
    }
    return false;
  }, [leagueDetails, user]);

  if (isLoadingPage && !leagueDetails) {
    return ( <div className="flex justify-center items-center min-h-[calc(100vh-100px)] p-4"> <Spinner size="lg" color="text-accent-yellow" /> <p className="ml-3 text-gray-300">Loading Roster Management...</p> </div> );
  }
  if (pageError && !leagueDetails && !isLoadingPage) {
    return <div className="p-8 text-center text-secondary bg-red-900/30 rounded">{pageError}</div>;
  }
  if (!leagueDetails) {
    return <div className="p-8 text-center text-gray-400">Loading league details...</div>;
  }
   if (user?.id !== leagueDetails.owner_id && !isLoadingPage) {
    return <div className="p-8 text-center text-secondary">Access Denied: You are not the manager of this league.</div>;
  }

  return (
    <div className="min-h-screen bg-primary p-4 md:p-6 text-gray-200 font-body">
      <header className="mb-6 text-center">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-accent-light-blue">Roster Input: <span className="text-accent-yellow">{leagueDetails.name}</span></h1>
        <p className="text-sm text-gray-400">Offline Draft - Add {MAX_ROSTER_SIZE_LM} players to each team's roster.</p>
         <div className="mt-2 space-x-2">
            <Link to={`/leagues/${numericLeagueId}/admin/set-initial-waiver-order`} className="text-xs sm:text-sm text-accent-yellow hover:underline">Set Waiver Order</Link>
            <span className="text-gray-500">|</span>
            <Link to={`/leagues/${numericLeagueId}/setup`} className="text-xs sm:text-sm text-accent-light-blue hover:text-accent-yellow underline">Back to League Setup</Link>
        </div>
      </header>
      {(pageError) && <p className="my-2 text-sm text-center text-secondary bg-red-900/30 border border-secondary rounded p-1.5">{pageError}</p>}
      {finalizeError && <p className="my-2 text-sm text-center text-secondary bg-red-900/30 border border-secondary rounded p-1.5">{finalizeError}</p>}
      {finalizeSuccessMessage && <p className="my-2 text-sm text-center text-green-400 bg-green-900/50 border border-green-500 rounded p-1.5">{finalizeSuccessMessage}</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <div className="md:col-span-1 bg-surface-card p-4 rounded-lg shadow-lg">
          <h2 className="text-lg font-semibold text-accent-light-blue mb-3">1. Select Team to Edit</h2>
          <select value={selectedTeam?.id || ''} onChange={(e) => handleSelectTeamChange(e.target.value)} className="w-full p-2 rounded bg-primary border border-gray-600 text-gray-100 mb-4 focus:ring-accent-yellow focus:border-accent-yellow" disabled={isLoadingPage || isUpdatingRoster || isFinalizing}>
            <option value="">-- Select a Team --</option>
            {teams.map(team => (<option key={team.id} value={team.id}>{team.name} {team.owner_username ? ` (${team.owner_username})` : ''}</option>))}
          </select>
          {selectedTeam && (
            <div>
              <h3 className="text-md font-semibold text-accent-yellow mb-2 flex justify-between"><span>Editing: {selectedTeam.name}</span><span>({fetchedTeamRoster.length}/{MAX_ROSTER_SIZE_LM})</span></h3>
              {isLoadingSelectedTeamRoster && (<div className="text-center my-1"><Spinner size="sm" /> <span className="text-xs italic">Loading roster...</span></div>)}
              {!isLoadingSelectedTeamRoster && fetchedTeamRoster.length === 0 && !(pageError && selectedTeam) && (<p className="text-xs text-gray-400 italic">No players added yet.</p>)}
              <ul className="space-y-1 max-h-[50vh] overflow-y-auto scrollbar-mobile-hide pr-1">
                {fetchedTeamRoster.slice().sort((a,b) => formatPlayerDisplayName(a.player).localeCompare(formatPlayerDisplayName(b.player))).map(rp => {
                    if (!rp.player) return null;
                    return (
                      <li key={rp.player.id} className="flex justify-between items-center bg-primary/50 p-1.5 rounded text-xs">
                        <span className="truncate" title={formatPlayerDisplayName(rp.player)}>{formatPlayerDisplayName(rp.player)} ({rp.player.fantasy_position_in_league || rp.player.primary_position || 'N/A'})</span>
                        <button onClick={() => handleRemovePlayerFromSelectedTeam(rp.player!.id)} disabled={isUpdatingRoster || isFinalizing} className="text-red-400 hover:text-red-300 text-xs px-1.5 py-0.5 rounded bg-red-700/50 hover:bg-red-600/50 disabled:opacity-50 flex items-center">
                            {isUpdatingRoster && fetchedTeamRoster.find(r => r.player?.id === rp.player!.id) && <Spinner size="sm" color="text-red-200" className="inline-block mr-1 h-3 w-3" />} Remove
                        </button>
                      </li>
                    );
                })}
              </ul>
            </div>
          )}
        </div>
        <div className="md:col-span-2 bg-surface-card p-4 rounded-lg shadow-lg flex flex-col min-h-[calc(60vh+100px)]">
           <h2 className="text-lg font-semibold text-accent-light-blue mb-3">2. Find Players</h2>
          {!selectedTeam && (<div className="text-center text-gray-400 italic mt-10 flex-grow flex items-center justify-center"><p>Select a team to start adding players.</p></div>)}
          {selectedTeam && (
            <>
              <LeaguePlayerPool
                mode="league"
                isLmPreDraftMode={isLmPreDraftContext}
                players={availablePlayersPool}
                onAddPlayerToRoster={handleAddPlayerToSelectedTeam}
                playerFetchError={pageError}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                positionFilter={positionFilter}
                onPositionFilterChange={setPositionFilter}
                sortConfig={sortConfig}
                onSortChange={(sortBy, sortOrder) => setSortConfig({ sortBy, sortOrder })}
                currentRosterPlayerIds={currentTeamRosterPlayerIds}
                availabilityFilter="available"
                onAvailabilityFilterChange={() => {}}
              />
              <div className="mt-4 text-center">
                {hasMorePlayersInPool && !isLoadingMorePlayers && (
                  <button onClick={handleLoadMorePlayers} className="bg-accent-light-blue hover:bg-blue-500 text-primary font-semibold py-2 px-6 rounded-md transition-colors">
                    Load More Players ({availablePlayersPool.length}/{totalAvailablePlayersInPool})
                  </button>
                )}
                {isLoadingMorePlayers && (<div className="flex justify-center items-center py-2"><Spinner size="sm" /><span className="ml-2 text-gray-400">Loading more...</span></div>)}
                {!hasMorePlayersInPool && availablePlayersPool.length > 0 && (<p className="text-sm text-gray-500 italic">All available players loaded.</p>)}
              </div>
            </>
          )}
        </div>
      </div>
      {leagueDetails && (
        <div className="mt-8 pt-6 border-t border-gray-700 max-w-xl mx-auto">
          <h2 className="text-xl font-heading font-bold text-accent-light-blue mb-3 text-center">3. Finalize League Setup</h2>
          <p className="text-sm text-gray-400 mb-4 text-center">Once all {leagueDetails.max_teams} teams have joined, rosters are full ({MAX_ROSTER_SIZE_LM} players each), and waiver priorities are set, you can start the league season.</p>
          <button
            onClick={handleFinalizeAndStartLeague}
            disabled={!canAttemptFinalize || isFinalizing || isLoadingPage || leagueDetails.draft_status === "REGULAR_SEASON" || leagueDetails.draft_status === "PLAYOFFS" || leagueDetails.draft_status === "COMPLETED" }
            className={`w-full py-3 px-4 rounded-lg font-heading font-bold text-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-surface-card ${
              (!canAttemptFinalize || isFinalizing || isLoadingPage || leagueDetails.draft_status === "REGULAR_SEASON" || leagueDetails.draft_status === "PLAYOFFS" || leagueDetails.draft_status === "COMPLETED" )
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-secondary hover:bg-red-700 text-white animate-pulse'
            }`}
            title={ isLoadingPage ? "Loading team data." : !teamDataIsComplete ? INCOMPLETE_TEAM_DATA_MESSAGE : !leagueIsFull ? `League not full (${leagueDetails.teams.length}/${leagueDetails.max_teams}).` : (leagueDetails.draft_status === "REGULAR_SEASON" || leagueDetails.draft_status === "PLAYOFFS" || leagueDetails.draft_status === "COMPLETED") ? `League already started (Status: ${leagueDetails.draft_status})` : "Finalize Rosters & Start League Season" }
          >
            {isFinalizing ? <Spinner size="sm" color="text-white" className="mr-2 inline-block"/> : null}
            {isFinalizing ? 'Starting League...' : 'Finalize & Start League'}
          </button>
          {!isLoadingPage && !teamDataIsComplete && (<p className="text-xs text-secondary mt-1 text-center italic">{INCOMPLETE_TEAM_DATA_MESSAGE}</p>)}
          {teamDataIsComplete && !leagueIsFull && (<p className="text-xs text-yellow-400 mt-1 text-center italic">Action enabled once all {leagueDetails.max_teams} teams have joined.</p>)}
          {(leagueDetails.draft_status === "REGULAR_SEASON" || leagueDetails.draft_status === "PLAYOFFS" || leagueDetails.draft_status === "COMPLETED") && (<p className="text-xs text-green-400 mt-1 text-center italic">League season is already in progress or completed.</p>)}
        </div>
      )}
    </div>
  );
};

export default LMRosterManagementPage;
